import { client } from './client'
import type { WorkflowDefinition, WorkflowDefinitionSummary, WorkflowValidationResult, CreateWorkflowDefinitionRequest, UpdateWorkflowDefinitionRequest, WorkflowDefinitionFilters, PaginationParams, PagedResult, TriggerWorkflowResponse, WorkflowTriggerSchema, WorkflowYamlSnapshot } from '@/types'
import type { ClaudeRunOverrides } from '@/types/claude'
import type { CopilotRunOverrides } from '@/types/copilot'

export interface WorkflowDefinitionEntityContext {
  linkedEntityType?: 'WorkItem' | 'Intent' | 'Project'
  linkedEntityId?: string
}

interface WorkflowDefinitionQueryParams {
  search?: string
  isActive?: boolean
  isSystem?: boolean
  linkedEntityType?: 'WorkItem' | 'Intent' | 'Project'
  linkedEntityId?: string
  maxResultCount?: number
  skipCount?: number
}

function buildQueryParams(filters?: WorkflowDefinitionFilters, pagination?: PaginationParams, entityContext?: WorkflowDefinitionEntityContext): WorkflowDefinitionQueryParams {
  const params: WorkflowDefinitionQueryParams = {}

  if (filters?.search) {
    params.search = filters.search
  }
  if (filters?.status === 'active') {
    params.isActive = true
  } else if (filters?.status === 'inactive') {
    params.isActive = false
  }
  if (filters?.type === 'system') {
    params.isSystem = true
  } else if (filters?.type === 'custom') {
    params.isSystem = false
  }

  if (entityContext?.linkedEntityType) {
    params.linkedEntityType = entityContext.linkedEntityType
  }
  if (entityContext?.linkedEntityId) {
    params.linkedEntityId = entityContext.linkedEntityId
  }

  if (pagination?.maxResultCount) {
    params.maxResultCount = pagination.maxResultCount
  }
  if (pagination?.skipCount) {
    params.skipCount = pagination.skipCount
  }

  return params
}

export const workflowDefinitionsApi = {
  getAll: (projectId: string, filters?: WorkflowDefinitionFilters, pagination?: PaginationParams, entityContext?: WorkflowDefinitionEntityContext) =>
    client.get<PagedResult<WorkflowDefinitionSummary>>(`/projects/${projectId}/workflow-definitions`, {
      params: buildQueryParams(filters, pagination, entityContext),
    }).then(r => r.data),

  getById: (projectId: string, id: string) =>
    client.get<WorkflowDefinition>(`/projects/${projectId}/workflow-definitions/${id}`).then(r => r.data),

  create: (projectId: string, data: CreateWorkflowDefinitionRequest) =>
    client.post(`/projects/${projectId}/workflow-definitions`, data).then(r => r.data),

  update: (projectId: string, id: string, data: UpdateWorkflowDefinitionRequest) =>
    client.put<{ workflowDefinitionId: string; version: string }>(`/projects/${projectId}/workflow-definitions/${id}`, data).then(r => r.data),

  duplicate: (projectId: string, id: string) =>
    client.post(`/projects/${projectId}/workflow-definitions/${id}/duplicate`).then(r => r.data),

  toggleActive: (projectId: string, id: string) =>
    client.patch(`/projects/${projectId}/workflow-definitions/${id}/toggle-active`).then(r => r.data),

  delete: (projectId: string, id: string) =>
    client.delete(`/projects/${projectId}/workflow-definitions/${id}`).then(r => r.data),

  validateYaml: (projectId: string, yamlContent: string) =>
    client.post<WorkflowValidationResult>(`/projects/${projectId}/workflow-definitions/validate`, { yamlContent }).then(r => r.data),

  trigger: (projectId: string, name: string, data: { inputValuesJson?: string; linkedEntityId?: string; linkedEntityType?: 'WorkItem' | 'Intent' | 'Project'; claudeOverrides?: ClaudeRunOverrides; copilotOverrides?: CopilotRunOverrides; sandboxImageId?: string | null; forceRerun?: boolean; agentProviderOverride?: string }) =>
    client.post<TriggerWorkflowResponse>(`/projects/${projectId}/workflow-definitions/${name}/trigger`, data).then(r => r.data),

  getTriggerSchema: (projectId: string, name: string) =>
    client.get<WorkflowTriggerSchema>(`/projects/${projectId}/workflow-definitions/${name}/trigger-schema`).then(r => r.data),

  getYamlSnapshots: (projectId: string, id: string) =>
    client.get<WorkflowYamlSnapshot[]>(`/projects/${projectId}/workflow-definitions/${id}/yaml-snapshots`).then(r => r.data),
}
